/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  SimpleChanges,
  viewChild,
  viewChildren,
} from '@angular/core';
import {
  CalendarClickEvent,
  CalendarDaySection,
  CalendarDaySubSection,
  CalendarDropTask,
  CalendarTask,
  CalendarTaskHeaderGroup,
  CalendarTasksGroup,
  CommonUtils,
  CustomDatePipe,
  HourSelectionEvent,
  Time,
  WeekSelectionEvent,
} from '@infodata-group/inf-commons';
import { TranslateService } from '@ngx-translate/core';
import dayjs, { Dayjs } from 'dayjs';
import jsPDF from 'jspdf';
import { CalendarHorizontalResourcesComponent } from '../calendar-horizontal-resources/calendar-horizontal-resources.component';
import { CalendarResourceLine } from '../calendar-resource-line';
import { CalendarView } from '../calendar-view';
import { DayRowDirective } from '../calendar-view-day/day-row.directive';
import { PdfHelper } from '../export/pdf-helper';
import { Telework } from '../telework';
import { DayNotWorkService } from '../day-not-work.service';

@Component({
  selector: 'infl-calendar-view-day-by-hour-horizontal',
  templateUrl: './calendar-view-day-by-hour-horizontal.component.html',
  styleUrls: [
    './calendar-view-day-by-hour-horizontal.component.scss',
    '../compatibility.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    CalendarHorizontalResourcesComponent,
    DayRowDirective,
    CustomDatePipe,
  ],
})
export class CalendarViewDayByHourHorizontalComponent
  implements OnInit, OnChanges, OnDestroy, CalendarView, AfterViewInit
{
  public resources = input<CalendarTaskHeaderGroup[]>();
  public showResources = input(false);
  public start = input({ hours: 7, minutes: 0 });
  public end = input({ hours: 17, minutes: 30 });
  public hourDivCount = input(2);
  public outsideDraggingTask = input<{ task: CalendarTask; shift: number }>();
  public dragByOutside = input<CalendarDropTask<any>>();
  public displayTime = input(true);
  public canDrag = input(true);
  public canResize = input(true);
  public canSelect = input(true);
  public tasks = input<CalendarTask[]>();
  public resourceLines = input<CalendarResourceLine[]>();
  public onWeek = input(false);
  public day = input<Date>();
  public sectionHeight = input(40);
  public taskHeight = input(30);
  public enable = input(true);
  public resourceTitle = input<string>();
  public cellWidth = input<any>();
  public teleworks = input<Telework[]>(null);

  public hourSelection = output<WeekSelectionEvent>();
  public hourPreselection = output<WeekSelectionEvent>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<{
    date: Date;
    group: { header: string; group: string };
  }>();
  public taskClick = output<CalendarClickEvent>();
  public taskDblClick = output<CalendarClickEvent>();
  public taskContextMenu = output<CalendarTask>();
  public clickOnSelection = output<WeekSelectionEvent>();
  public dblClickOnSelection = output<WeekSelectionEvent>();
  public dragTask = output<{
    task: CalendarTask;
    shift: number;
  }>();
  public dropOutside = output();
  public taskHover = output<CalendarTask>();

  protected header = viewChild<ElementRef>('header');
  public tableBody = viewChild<ElementRef>('tableBody');
  protected dayCells = viewChildren<ElementRef>('dayRow');
  protected resourceComponent = viewChild(CalendarHorizontalResourcesComponent);

  public zones: CalendarDaySection[] = [];
  public sectionCount: number;
  public isLoading = false;
  public viewTasks: { task: CalendarTask; style: any }[];
  public draggingTask: { task: CalendarTask; style: any };
  public isSafari: boolean;
  public selection: { start: Time; end: Time; resource: CalendarTasksGroup } = {
    start: null,
    end: null,
    resource: null,
  };
  public selectedZones: CalendarDaySubSection[] = [];
  public selectedGroup: CalendarTasksGroup;
  public hourLineStyle: any;
  public isCurrentDay: boolean;
  public calendarBodyMinWidth: number;
  public calendarBodyWithOnWeek: number;
  public isChanging: boolean;
  public lines: CalendarResourceLine[];

  public weekNumber = computed(() => {
    return this.dateWeek(this.day());
  });

  public dayToDayJs = computed(() => {
    return dayjs(this.day());
  });

  private interval: any;
  private dragShift: number;
  private timer: any;
  private saveSelection: {
    start: Time;
    end: Time;
    resource: CalendarTasksGroup;
  };
  private resizeStart: number;
  private resizeTask: {
    border: 'left' | 'right';
    task: CalendarTask;
    element: any;
  };
  private previousTaskHeight: string;
  private baseWidth: number;
  private baseLeft: number;
  private lastCellDraggedOver: DayRowDirective;

  private cellHeaders = viewChildren<ElementRef>('cellHeader');
  protected showFullTime = computed(() =>
    this.cellHeaders().map(
      (cellHeader) =>
        this.cellWidth() === 'auto' ||
        cellHeader.nativeElement.clientWidth >= 40,
    ),
  );

  public readonly translate = inject(TranslateService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly dayNotWorkService = inject(DayNotWorkService);

  public backgroundColorDay = computed(() => {
    if (this.day() && !this.dayNotWorkService.isDayWork(this.day())) {
      return this.dayNotWorkService.dayNotWorkColor;
    }
    return 'var(--white)';
  });

  constructor() {
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  ngOnInit() {
    this.isCurrentDay = CommonUtils.compareDate(this.day(), new Date());
    if (this.isCurrentDay) {
      this.createHourLineStyle();
    }
    this.initializeMinWidth();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      Object.hasOwn(changes, 'resources') ||
      Object.hasOwn(changes, 'hourDivCount')
    ) {
      if (this.resources()) {
        this.buildZone();
        this.isLoading = false;
      }
    }
    if (
      Object.hasOwn(changes, 'hourDivCount') ||
      Object.hasOwn(changes, 'cellWidth') ||
      Object.hasOwn(changes, 'sectionHeight')
    ) {
      this.checkWidth();
      this.computeHourLineStyle();
      this.isChanging = true;
      setTimeout(() => {
        this.load();
        this.isChanging = false;
      }, 1000);
    }
    if (
      Object.hasOwn(changes, 'tasks') ||
      Object.hasOwn(changes, 'resourceLines') ||
      Object.hasOwn(changes, 'day')
    ) {
      this.load();
      this.draggingTask = null;
      this.clearLastCellHighlighted();
    }
  }

  ngAfterViewInit() {
    this.checkWidth();
    this.changeDetector.detectChanges();
  }

  onTaskClick(event: MouseEvent, task: CalendarTask, dblClick?: boolean) {
    if (this.resizeTask) {
      return;
    }
    if (dblClick) {
      this.taskDblClick.emit(new CalendarClickEvent(event, task));
    } else {
      this.taskClick.emit(new CalendarClickEvent(event, task));
    }
  }

  onDragStart(task: { task: CalendarTask; style: any }) {
    if (this.canDrag()) {
      this.draggingTask = task;
      this.dragShift = 0;
      this.dragTask.emit({
        task: this.draggingTask.task,
        shift: this.dragShift,
      });
      this.disablePointerEvent(task);
    }
  }

  allowDrop(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(
    event: DragEvent,
    defaultHour: number,
    defaultMin: number,
    group: string,
    resource: string,
  ) {
    const elements = document.elementsFromPoint(event.x, event.y);
    const sub = elements.find((elem) => elem.className.indexOf('sub') >= 0);
    const hour = sub ? parseInt(sub['dataset'].hour, 10) : defaultHour;
    const minutes = sub ? parseInt(sub['dataset'].time, 10) : defaultMin;
    const section = this.zones.find((zone) => zone.hour === hour);
    const subSection = section.zones.find((s) => s.minutes === minutes);
    if (section && subSection) {
      this.onDropTask(section, subSection, group, resource);
    }
  }

  onDropTask(
    cell: CalendarDaySection,
    sub: CalendarDaySubSection,
    group: string,
    resource: string,
  ) {
    if (this.canDrag()) {
      let task;
      let m: Dayjs;
      if (this.draggingTask) {
        task = this.draggingTask.task;
        m = task.dateStart.clone();
      } else if (this.dragByOutside()) {
        const d = new Date(this.day());
        d.setHours(cell.hour, sub.minutes);
        this.taskCreation.emit({
          date: d,
          group: { header: group, group: resource },
        });
        return;
      } else if (this.outsideDraggingTask()) {
        task = this.outsideDraggingTask().task;
        m = task.dateStart
          .clone()
          .year(this.day().getFullYear())
          .month(this.day().getMonth())
          .date(this.day().getDate());
      }
      if (task) {
        let minutes = cell.hour * 60 + sub.minutes;
        const rest = minutes % (60 / this.hourDivCount());
        minutes -= rest;
        m = m.hour(Math.floor(minutes / 60)).minute(minutes % 60);
        task.updateDateStart(m);
        if (task.group.header !== group || task.group.group !== resource) {
          this.changeTaskGroup(task, group, resource);
        }
        task.group = { header: group, group: resource };
        this.taskUpdate.emit(task);
        this.draggingTask = null;
        this.dropOutside.emit();
      }
    }
  }

  startResize(
    event: MouseEvent,
    task: CalendarTask,
    direction: 'left' | 'right',
  ) {
    if (this.canResize()) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();
      this.resizeStart = event.x;
      const target = (<any>event.target).closest('.task');
      this.previousTaskHeight = target.style.height;
      target.style.height = `${this.sectionHeight()}px`;
      target.style.opacity = '0.3';
      this.resizeTask = { element: target, task: task, border: direction };
      this.baseWidth = target.clientWidth;
      this.baseLeft = target.offsetLeft;
    }
  }

  onMouseMove(event: MouseEvent) {
    if (this.resizeTask) {
      if (this.resizeTask.border === 'right') {
        this.resizeTask.element.style.width = `${
          this.baseWidth + (event.x - this.resizeStart)
        }px`;
      } else {
        this.resizeTask.element.style.left = `${
          this.baseLeft + (event.x - this.resizeStart)
        }px`;
        this.resizeTask.element.style.width = `${
          this.baseWidth - (event.x - this.resizeStart)
        }px`;
      }
    }
  }

  public generateTasks() {
    if (!this.onWeek()) {
      this.generateResourceLines();
    } else {
      this.lines = this.resourceLines().concat([]);
    }
    this.viewTasks = [];
    let top = this.showResources() ? 90 : 60;
    const pxPerMinute = this.getPixelPerMinute();
    for (const header of this.resources()) {
      top += 40;
      if (header.isOpened) {
        for (const group of header.tasksGroups) {
          const line = this.lines.find(
            (l) => l.header === header && l.resource === group,
          );
          const rows = line.getTasks(this.day());
          for (let i = 0; i < rows.length; ++i) {
            for (const task of rows[i]) {
              const canAdd = this.compareDateTime(task);
              if (canAdd) {
                const style = this.createStyle(task, pxPerMinute, top);
                style['top.px'] = top + (this.taskHeight() + 5) * i;
                this.viewTasks.push({ task: task, style: style });
              }
            }
          }
          const height =
            this.sectionHeight() +
            (line.height > 0 ? line.height - 1 : 0) * this.taskHeight();
          if (!this.onWeek()) {
            if (group.height < height) {
              group.height = height;
            }
          }
          top += group.height;
        }
      }
    }
  }

  selectZone(
    event: MouseEvent,
    resource: CalendarTasksGroup,
    hour: number,
    sub: CalendarDaySubSection,
  ) {
    if (this.canSelect()) {
      if (!sub.selected || this.selectedGroup !== resource) {
        this.selectedGroup = resource;
        event.stopPropagation();
        event.preventDefault();
        for (const section of this.selectedZones) {
          section.selected = false;
        }
        this.selectedZones = [sub];
        sub.select(true);
        this.selection.start = { hours: hour, minutes: sub.minutes };
      } else if (event.which === 3 && sub.selected) {
        const selection = new HourSelectionEvent(this.saveSelection, event);
        this.clickOnSelection.emit(
          new WeekSelectionEvent(this.day(), selection),
        );
      }
    }
  }

  overZone(
    event: MouseEvent,
    hour: number,
    minutes: number,
    sub: CalendarDaySubSection,
    resource: CalendarTasksGroup,
  ) {
    if (this.selection.start) {
      for (const section of this.selectedZones) {
        section.selected = false;
      }
      this.selection.end = { hours: hour, minutes: minutes };
      for (const zone of this.zones) {
        const sections = zone.select(this.selection.start, this.selection.end);
        if (sections) {
          for (const s of sections) {
            this.selectedZones.push(s);
          }
        }
      }
    } else if (!(this.selection.start && this.selection.end)) {
      const selection = { start: null, end: null, resource: resource };
      selection.start = { hours: hour, minutes: minutes };
      selection.end = { hours: hour, minutes: minutes + sub.minutes };
      const preselection = new HourSelectionEvent(selection, event);
      this.hourPreselection.emit(
        new WeekSelectionEvent(this.day(), preselection),
      );
    }
  }

  deselect(): void {
    for (const section of this.selectedZones) {
      section.selected = false;
    }
    this.selectedZones = [];
  }

  onDblClick(event: MouseEvent) {
    if (this.saveSelection) {
      const selection = new HourSelectionEvent(this.saveSelection, event);
      this.dblClickOnSelection.emit(
        new WeekSelectionEvent(this.day(), selection),
      );
    }
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  /**
   * enable pointerEvent to catch drop event on zone, not on task
   */
  public enablePointerEvent() {
    this.viewTasks.forEach((t) => {
      t.style['pointer-events'] = 'auto';
    });
  }

  public findTask(id: string) {
    return this.viewTasks.find((vt) => vt.task.id === id);
  }

  onHover(task: CalendarTask) {
    this.taskHover.emit(task);
  }

  getScrollPosition(): { x: number; y: number } {
    return null;
  }

  setScrollPosition(x: number, y: number) {
    // DO Nothing
  }

  private buildZone() {
    this.zones = [];
    this.sectionCount = 0;
    let i = this.start().hours;
    for (i; i < this.end().hours; ++i) {
      const section = new CalendarDaySection(i);
      section.generateSubZones(this.start(), this.end(), this.hourDivCount());
      this.sectionCount += section.length;
      this.zones.push(section);
    }
    if (this.end().minutes > 0) {
      const section = new CalendarDaySection(i);
      section.generateSubZones(this.start(), this.end(), this.hourDivCount());
      this.sectionCount += section.length;
      this.zones.push(section);
    }
  }

  @HostListener('document:mouseup') stopResize() {
    if (this.resizeTask) {
      this.resizeTask.element.style.opacity = '1';
      this.resizeTask.element.style.height = this.previousTaskHeight;
      const width = this.resizeTask.element.clientWidth;
      const pixelByMinutes = this.getPixelPerMinute();
      const minutePerZone = 60 / this.hourDivCount();
      const duration =
        Math.round(width / pixelByMinutes / minutePerZone) * minutePerZone;
      const task = this.resizeTask.task;
      if (this.resizeTask.border === 'right') {
        task.dateEnd = task.dateStart.clone().add(duration, 'm');
        task.around('end', minutePerZone);
      } else {
        task.dateStart = task.dateEnd.clone().subtract(duration, 'm');
        task.around('start', minutePerZone);
      }
      this.taskUpdate.emit(task);
      setTimeout(() => {
        this.resizeTask = null;
      }, 100);
      this.generateTasks();
    }
  }

  private getPixelPerMinute() {
    if (!this.tableBody()) {
      return 0;
    }
    const start = this.start().hours * 60 + this.start().minutes;
    const end = this.end().hours * 60 + this.end().minutes;
    const diff = end - start;
    return this.tableBody().nativeElement.clientWidth / diff;
  }

  private createStyle(
    task: CalendarTask,
    pxByMinute: number,
    top: number,
  ): any {
    let offsetLeft = 0;

    if (CommonUtils.compareDate(task.dateStart.toDate(), this.day())) {
      const minuteStart = this.start().hours * 60 + this.start().minutes;
      const taskStart = task.dateStart.get('h') * 60 + task.dateStart.get('m');
      offsetLeft = (taskStart - minuteStart) * pxByMinute;
      if (offsetLeft < 0) {
        offsetLeft = 0;
      }
    }
    const duration = task.getMinutesForDay(
      this.day(),
      this.start(),
      this.end(),
    );
    let width;

    if (duration === 'full') {
      width = `calc(100% - ${offsetLeft}px)`;
    } else {
      width = duration * pxByMinute;
    }
    const style = {
      'top.px': top,
      'left.px': offsetLeft,
      borderColor: task.borderColor ?? task.backgroundColor,
      color: task.color,
      'height.px': this.taskHeight(),
    };
    if (duration === 'full') {
      style['width'] = width;
    } else {
      style['width.px'] = width;
    }
    return style;
  }

  private loadTaskAfterView() {
    this.isLoading = true;
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      if (this.header) {
        this.generateTasks();
        this.isLoading = false;
      } else {
        this.loadTaskAfterView();
      }
    }, 400);
  }

  private checkTime() {
    const start = CommonUtils.convertStrToTimeInMillis(
      CommonUtils.convertTimeToString(this.selection.start),
    );
    const end = CommonUtils.convertStrToTimeInMillis(
      CommonUtils.convertTimeToString(this.selection.end),
    );
    if (start >= end) {
      const temp = this.selection.end;
      this.selection.end = this.selection.start;
      this.selection.start = temp;
    }
  }

  public stopSelection(event: MouseEvent) {
    if (this.selection.start) {
      if (!this.selection.end) {
        this.selection.end = this.selection.start;
      }
      this.checkTime();
      this.selection.resource = this.selectedGroup;
      this.setSelectionEnd(
        this.selection.end.hours,
        this.selection.end.minutes,
      );
      const selection = new HourSelectionEvent(this.selection, event);
      this.hourSelection.emit(new WeekSelectionEvent(this.day(), selection));
      this.saveSelection = this.selection;
    }
    this.selection = { start: null, end: null, resource: null };
  }

  private setSelectionEnd(hour: number, minutes: number) {
    let m = hour * 60 + minutes + 60 / this.hourDivCount();
    const h = Math.floor(m / 60);
    m = m % 60;
    this.selection.end = { hours: h, minutes: m };
  }

  private generateResourceLines() {
    this.lines = [];
    for (const header of this.resources()) {
      for (const resource of header.tasksGroups) {
        this.lines.push(
          new CalendarResourceLine(
            header,
            resource,
            [this.day()],
            this.sectionHeight(),
          ),
        );
      }
    }
  }

  private createHourLineStyle() {
    this.computeHourLineStyle();
    this.interval = setInterval(() => {
      this.computeHourLineStyle();
    }, 60000);
  }

  private computeHourLineStyle() {
    const d = new Date();
    const currentTime = d.getHours() * 60 + d.getMinutes();
    const start = this.start().hours * 60 + this.start().minutes;
    const end = this.end().hours * 60 + this.end().minutes;
    if (currentTime < end && currentTime > start) {
      setTimeout(() => {
        const m = (currentTime - start) * this.getPixelPerMinute();
        this.hourLineStyle = {
          left: m + 'px',
        };
      }, 400);
    } else {
      this.hourLineStyle = {
        display: 'none',
      };
    }
  }

  /**
   * disable pointerEvent to catch drop event on zone, not on task
   * @param task
   * @private
   */
  private disablePointerEvent(task: { task: CalendarTask; style: any }) {
    this.viewTasks.forEach((t) => {
      if (t.task.id !== task.task.id) {
        t.style['pointer-events'] = 'none';
      } else {
        // todo to improve when we have time
        setTimeout(() => {
          task.style['pointer-events'] = 'none';
        }, 1);
      }
    });
  }

  private changeTaskGroup(
    task: CalendarTask,
    headerKey: string,
    groupKey: string,
  ) {
    const group = this.getGroup(headerKey, groupKey);
    if (group) {
      group.addTask(task);
    }
    const oldGroup = this.getGroup(task.group.header, task.group.group);
    if (oldGroup) {
      oldGroup.removeTask(task);
    }
  }

  private getGroup(headerKey, groupKey): CalendarTasksGroup {
    const header = this.resources().find((res) => res.id === headerKey);
    if (header) {
      return header.tasksGroups.find((tg) => tg.id === groupKey);
    }
    return null;
  }

  /**
   * Set a minimal width for calendar body according to the number of zones (hour) and the number of hours division
   * 22 is the minimal width of an hour division
   * @private
   */
  private initializeMinWidth(): void {
    const width = this.cellWidth() === 'auto' ? 22 : this.cellWidth();
    this.calendarBodyMinWidth = this.zones.length * this.hourDivCount() * width;
  }

  private load() {
    if (this.header) {
      this.generateTasks();
    } else {
      this.loadTaskAfterView();
    }
  }

  private compareDateTime(task: CalendarTask): boolean {
    const date = new Date(this.day().getTime());
    const start = date.setHours(this.start().hours, this.start().minutes);
    const end = date.setHours(this.end().hours, this.end().minutes);

    const tStart = task.dateStart.toDate().getTime();
    const tEnd = task.dateEnd.toDate().getTime();
    const tooSoon = tEnd <= start;

    const tooLate = tStart >= end;

    return !tooSoon && !tooLate;
  }

  public scrollToDate(date: Date): void {
    if (CommonUtils.compareDate(date, this.day())) {
      const h = date.getHours();
      const cell = this.dayCells().find((c) => {
        return parseFloat(c.nativeElement.dataset.hour) === h;
      });
      if (cell) {
        cell.nativeElement.scrollIntoView({ block: 'center' });
      }
    }
  }

  private checkWidth() {
    this.initializeMinWidth();
    if (this.cellWidth() === 'auto' && this.tableBody()) {
      this.calendarBodyWithOnWeek = this.tableBody().nativeElement.clientWidth;
    } else {
      this.calendarBodyWithOnWeek = this.sectionCount * this.cellWidth();
    }
  }

  public toPdf(
    doc: jsPDF,
    pageWidth: number,
    x: number,
    lastPageHeight: number,
    onWeek?: boolean,
  ) {
    if (!onWeek) {
      lastPageHeight = this.resourceComponent().toPdf(doc);
    }
    if (!pageWidth) {
      pageWidth = doc.internal.pageSize.width - 240;
    }
    if (!x) {
      x = 230;
    }
    const sectionCount = this.zones.reduce((n, s) => (n += s.length), 0);
    const sectionWidth = pageWidth / sectionCount;
    const minuteWidth = pageWidth / (sectionCount * (60 / this.hourDivCount()));
    const pageCount = doc.getCurrentPageInfo().pageNumber;

    for (let i = 1; i <= pageCount; ++i) {
      doc.setPage(i);
      const height =
        i < pageCount ? doc.internal.pageSize.height - 20 : lastPageHeight;
      this.generatePDFTimeZone(doc, x, sectionWidth, height);
    }
    doc.setPage(1);

    this.generatePDFTasks(doc, x + 10, minuteWidth);
  }

  private generatePDFTasks(doc: jsPDF, x: number, minuteWidth: number) {
    let y = 70;
    let group = null;
    let page = 1;
    for (const res of this.lines) {
      if (res.header !== group) {
        group = res.header;
        y += 30;
      }
      let rowIndex = 0;
      for (const rows of res.getTasks(this.day())) {
        for (const task of rows) {
          const start =
            task.dateStart.get('h') * 60 +
            task.dateStart.get('m') -
            (this.start().hours * 60 + this.start().minutes);
          const offsetLeft = start * minuteWidth + x;
          const taskMin = task.getMinutesForDay(
            this.day(),
            this.start(),
            this.end(),
          );
          if (typeof taskMin === 'number') {
            PdfHelper.setFillColor(doc, task.backgroundColor);
            doc.rect(
              offsetLeft,
              y + rowIndex * 20,
              taskMin * minuteWidth,
              18,
              'FD',
            );
            const text = PdfHelper.generateTextLine(
              doc,
              task.label,
              taskMin * minuteWidth - 20,
              18,
            );
            doc.text(text, offsetLeft + 10, y + 13 + rowIndex * 20);
          }
        }
        ++rowIndex;
      }
      y += (res.height > 0 ? res.height : 1) * 20 + 10;
      if (y > doc.internal.pageSize.height - 60) {
        y = 70;
        ++page;
        doc.setPage(page);
      }
    }
  }

  private generatePDFTimeZone(
    doc: jsPDF,
    x: number,
    sectionWidth: number,
    height: number,
  ) {
    const pageWidth = doc.internal.pageSize.width - 240;
    const docHeight = height - 40;
    const y = 40;
    doc.rect(x, 10, pageWidth, 30);
    doc.text(CommonUtils.convertDateToString(this.day()), x + 20, y - 10);

    for (const time of this.zones) {
      doc.rect(x, y, sectionWidth * time.length, 30);
      doc.text(time.time, x + 2, y + 20);
      for (let i = 0; i < time.length - 1; ++i) {
        doc.setDrawColor(CommonUtils.color('grey-c8'));
        doc.setLineDashPattern([2.5], 2);
        doc.line(x + sectionWidth, y, x + sectionWidth, y + docHeight);
        x += sectionWidth;
      }
      doc.setDrawColor(CommonUtils.color('black'));
      doc.setLineDashPattern(null, 0);
      doc.line(x + sectionWidth, y, x + sectionWidth, y + docHeight);
      x += sectionWidth;
    }
  }

  private clearLastCellHighlighted() {
    if (this.lastCellDraggedOver) {
      this.lastCellDraggedOver.isHovered = false;
      this.lastCellDraggedOver = null;
    }
  }

  onDragLeaveTbody() {
    this.clearLastCellHighlighted();
  }

  onDragOver(cell: DayRowDirective) {
    this.clearLastCellHighlighted();
    this.lastCellDraggedOver = cell;
    cell.isHovered = true;
  }

  public dateWeek(date: Date): string {
    const tempDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    const day = tempDate.getUTCDay() || 7;
    tempDate.setUTCDate(tempDate.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
    const weekNumber = Math.ceil(
      ((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
    );
    return weekNumber < 10 ? '0' + weekNumber : '' + weekNumber;
  }
}
